import { useState } from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { pickLocale, type Characteristic, type Value } from '../schema';
import { usePriceBreakdown } from '../pricing/PriceContext';
import { inputTypeForValidator } from '../validation/validate';
import { formatBound } from '../i18n/formatBound';
import { formatCurrency } from '../pricing/format';
import { useComponent, useComponentSettings } from '../state/ComponentContext';
import { useConfigentoStoreRef } from '../state/StoreContext';
import { useRuleResults } from '../rules/RuleResultsContext';
import { useEvaluatedMap } from '../eval/EvalContext';
import { useTranslation } from '../i18n';
import { formatCalculatedValue } from './characteristics/formatCalculatedValue';
import type { SelectionsMap } from '../state/selections';
import type { EvaluatedMap } from '../eval/types';
import type { PriceLine } from '../pricing/types';
import { useCombinedSku } from './useCombinedSku';

interface SummaryPanelProps {
    locale: string | undefined;
    /**
     * Whether the panel starts expanded. Collapsed by default, which is the
     * right call when it sits under a long form — but a host that gave it its
     * own place on the page (`summaryMount`) usually wants it readable
     * straight away, with nothing to click first.
     *
     * Initial state only. Once the customer has opened or closed it, that is
     * their decision and a re-render does not undo it.
     */
    defaultOpen?: boolean;
}

interface SummaryRow {
    characteristicId: number;
    label: string;
    /** Either an array of value labels (multi-pick) or a single string. */
    value: string;
    /** Price contribution from this Characteristic, summed across all its
     *  price lines. `null` when nothing in `lines[]` belongs to this id. */
    amount: number | null;
}

/**
 * Collapsible per-Characteristic summary. Mirrors legacy
 * `configento/app/summary.js:renderSummary` — every visible Characteristic
 * (visibility = 'visible' or 'backend', the same set the legacy storefront
 * shows) gets a row with its current value plus any price contribution.
 * Hidden Characteristics and `shop`-only ones stay out so the customer
 * sees the same shape the legacy frontend produced.
 */
export function SummaryPanel({ locale, defaultOpen = false }: SummaryPanelProps) {
    const component = useComponent();
    const { lines, total, currency } = usePriceBreakdown();
    const { t, locale: uiLocale } = useTranslation();
    const { displayPrice, displayPriceIfZero } = useComponentSettings();
    const ruleResults = useRuleResults();
    const { blacklistedValues } = ruleResults;
    const store = useConfigentoStoreRef();
    const selections = useStore(store, useShallow((s) => s.selections));
    const evaluated = useEvaluatedMap();
    const [open, setOpen] = useState(defaultOpen);

    // Legacy parity (summary.js:43): summary shows visibility ∈ {visible,
    // backend}, EXCLUDES rule-blacklisted. Note backend stays IN — that's a
    // deliberate legacy choice. Additionally hide Characteristics whose
    // values are all blacklisted (the form already hides them; the summary
    // mirrors that).
    // Deliberately NOT legacy: Characteristics of a Group the admin hid
    // (`hide: 1`) stay out too. Legacy summary.js listed them, which showed
    // shoppers lines from a step they never saw. They still price and still
    // reach the order payload (assetPayload) — only the summary skips them.
    const visibleChars = component.groups
        .filter((g) => !g.hidden)
        .flatMap((g) => g.characteristics)
        .filter((c) => {
            if (c.visibility !== 'visible' && c.visibility !== 'backend') return false;
            if (ruleResults.hiddenCharacteristics.has(c.id)) return false;
            if ('values' in c && Array.isArray(c.values) && c.values.length > 0) {
                const hasUnblacklisted = c.values.some((v) => !blacklistedValues.has(v.id));
                if (!hasUnblacklisted) return false;
            }
            return true;
        });

    const rows = buildSummaryRows({
        characteristics: visibleChars,
        selections,
        evaluated,
        lines,
        blacklistedValues,
        locale,
        uiLocale,
    });

    const sku = useCombinedSku();
    const combinedSku = component.showCombinedSku ? sku : '';

    if (rows.length === 0) return null;
    const showAmount = (amount: number) =>
        displayPrice && (amount !== 0 || displayPriceIfZero);

    return (
        <section className="cfg-summary" data-configento-summary>
            <button
                type="button"
                className="cfg-summary__toggle"
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
            >
                <span>
                    {t('summary.title')} ({rows.length})
                </span>
                <span className="cfg-summary__chevron" aria-hidden="true">
                    {open ? '▾' : '▸'}
                </span>
            </button>
            {open && (
                <ul className="cfg-summary__list">
                    {rows.map((row) => (
                        <li
                            key={row.characteristicId}
                            className="cfg-summary__item"
                            data-configento-char-id={row.characteristicId}
                        >
                            <span className="cfg-summary__label">
                                <strong>{row.label}</strong>
                                {row.value && <span>{` · ${row.value}`}</span>}
                            </span>
                            {row.amount !== null && showAmount(row.amount) && (
                                <span className="cfg-summary__amount">
                                    {formatCurrency(row.amount, currency, uiLocale)}
                                </span>
                            )}
                        </li>
                    ))}
                    {combinedSku !== '' && (
                        <li className="cfg-summary__item cfg-summary__item--sku" data-configento-combined-sku>
                            <span className="cfg-summary__label">
                                <strong>{t('summary.sku')}</strong>
                                <span>{` · ${combinedSku}`}</span>
                            </span>
                        </li>
                    )}
                    {showAmount(total) && (
                        <li className="cfg-summary__item cfg-summary__item--total">
                            <span className="cfg-summary__label">{t('summary.total')}</span>
                            <span className="cfg-summary__amount">
                                {formatCurrency(total, currency, uiLocale)}
                            </span>
                        </li>
                    )}
                </ul>
            )}
        </section>
    );
}

function buildSummaryRows(args: {
    characteristics: ReadonlyArray<Characteristic>;
    selections: SelectionsMap;
    evaluated: EvaluatedMap;
    lines: ReadonlyArray<PriceLine>;
    blacklistedValues: ReadonlySet<number>;
    locale: string | undefined;
    uiLocale: string;
}): SummaryRow[] {
    const { characteristics, selections, evaluated, lines, blacklistedValues, locale, uiLocale } =
        args;

    // Pre-aggregate price-line amounts by characteristic id.
    const amountByChar = new Map<number, number>();
    for (const line of lines) {
        const prev = amountByChar.get(line.characteristic.id) ?? 0;
        amountByChar.set(line.characteristic.id, prev + line.amount);
    }

    const out: SummaryRow[] = [];
    for (const c of characteristics) {
        // Visibility / rule-cascade filtering happens upstream (see SummaryPanel
        // visibleChars). Here we only convert each visible Characteristic into
        // a row.
        const value = formatCharacteristicValue(c, selections, evaluated, blacklistedValues, locale, uiLocale);
        // No value yet (untouched text, no selection, unresolved expression):
        // skip the row to avoid noisy empty entries. Matches legacy
        // `if (value == null || value == "") continue;`.
        if (value === '') continue;

        const label =
            pickLocale(c.title, locale) ?? c.altTitle ?? `#${c.id}`;

        out.push({
            characteristicId: c.id,
            label,
            value,
            amount: amountByChar.get(c.id) ?? null,
        });
    }
    return out;
}

function formatCharacteristicValue(
    c: Characteristic,
    selections: SelectionsMap,
    evaluated: EvaluatedMap,
    blacklistedValues: ReadonlySet<number>,
    locale: string | undefined,
    uiLocale: string,
): string {
    const sel = selections[c.id];

    switch (c.type) {
        case 'text':
        case 'area': {
            if (sel?.kind !== 'text') return '';
            const raw = sel.value.trim();
            // Group the thousands only where the field's own validator says
            // this is a number. A free-text field may hold an order reference
            // or a house number, and "Bestellung 10.000" would be an invented
            // separator in someone's data. The numeric validators are the only
            // place the Component states the intent, so they decide.
            if (!('textValidate' in c) || inputTypeForValidator(c.textValidate) !== 'number') {
                return raw;
            }
            // formatBound leaves anything non-numeric alone, which covers the
            // moment before validation runs: a half-typed "12x" stays "12x"
            // rather than vanishing.
            return formatBound(raw, uiLocale);
        }
        case 'select':
        case 'radiobuttons': {
            const id = sel?.kind === 'single' ? sel.valueId : null;
            if (id === null || id === undefined) return '';
            if (blacklistedValues.has(id)) return '';
            return labelOfValue(findValue(c, id), locale);
        }
        case 'multiselect':
        case 'checkbox':
        case 'listimage': {
            const ids =
                sel?.kind === 'multi'
                    ? sel.valueIds
                    : sel?.kind === 'single' && sel.valueId !== null
                      ? [sel.valueId]
                      : [];
            const labels = ids
                .filter((id) => !blacklistedValues.has(id))
                .map((id) => labelOfValue(findValue(c, id), locale))
                .filter((s) => s.length > 0);
            return labels.join(', ');
        }
        case 'multiquantity': {
            if (sel?.kind !== 'quantities') return '';
            const parts: string[] = [];
            for (const [idStr, qty] of Object.entries(sel.quantities)) {
                if (qty <= 0) continue;
                const id = Number(idStr);
                if (blacklistedValues.has(id)) continue;
                const label = labelOfValue(findValue(c, id), locale);
                if (label) parts.push(`${qty}× ${label}`);
            }
            return parts.join(', ');
        }
        case 'expression':
        case 'combi':
        case 'matrixvalue':
        case 'productattribute':
        case 'webservice': {
            const result = evaluated.get(c.id);
            return formatCalculatedValue(result ?? null, c.decimalPlace, uiLocale);
        }
        case 'package': {
            // Package label only when at least one Value is selected.
            const id = sel?.kind === 'single' ? sel.valueId : null;
            if (id === null || id === undefined) return '';
            return labelOfValue(findValue(c, id), locale);
        }
        // Render-only / no-data types — never show in summary.
        case 'static':
        case 'chart':
        case 'savebutton':
        case 'fileupload':
        case 'captcha':
        case 'unknown':
            return '';
    }
}

function findValue(c: Characteristic, id: number): Value | undefined {
    if (!('values' in c)) return undefined;
    return c.values.find((v) => v.id === id);
}

function labelOfValue(value: Value | undefined, locale: string | undefined): string {
    if (!value) return '';
    return pickLocale(value.title, locale) ?? value.value ?? '';
}
